import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CoverSurface } from '@/components/CoverSurface';
import { BRAND_KITS_UI_VISIBLE } from '@/features/brandKits/brandKitsGate';
import { useLibraryDateFormat } from '@/features/assets/libraryDate';
import { brandPrimaryHex, type BrandKit, type Project } from '@/types';

import { KebabMenu, useKebabMenu } from './KebabMenu';

import { pickCoverGradient } from './projectCovers';

/**
 * 專案清單項目(從 `ProjectListPage` 抽出的純結構重構,行為不變)
 * -----------------------------------------------------------------------------
 * grid 檢視的 `ProjectCard` 與 list 檢視的 `ProjectRow` — 兩者共用同一份資料 shape
 * (project + brandKit join)、同一組 kebab 選單與 cover gradient,刻意放同檔保持
 * 呈現對稱(改 meta 欄位兩邊一起改)。
 */

interface ProjectItemProps {
  project: Project;
  brandKit: BrandKit | null;
  onOpen: () => void;
  /** R292(FB-20260828-0008)— 開「編輯專案資訊」dialog。 */
  onEdit?: () => void;
  onDelete: () => void;
  deleting: boolean;
}

/**
 * D3 (3) — M-12 卡片設計
 * -----------------------------------------------------------------------------
 * 整卡 click → onOpen(進畫布);右上 kebab menu → [繼續編輯 / 刪除] dropdown。
 * 砍底部 button row(對齊 mockup line 217-232 純 cover + meta 設計);
 * 仍提供 `button[name="繼續編輯"]` / `button[name="刪除"]` 在 menu 內讓 a11y / test
 * 仍能 trigger 對應 action(menu 開啟後渲染)。
 */
export function ProjectCard({ project, brandKit, onOpen, onEdit, onDelete, deleting }: ProjectItemProps) {
  const { t } = useTranslation('projects');
  // CIS 日期寫法:`8月 3, 2026 14:22`(每個日期都要有年;舊的 `shortDate` 是 `2026/08/03 下午02:22`)
  const { dstamp } = useLibraryDateFormat();
  const [hover, setHover] = useState(false);
  const { menuOpen, setMenuOpen, menuRef } = useKebabMenu();
  const coverBg = pickCoverGradient(brandKit?.colors.primary);
  // ⚠️ `colors.primary` 是 **CSV 字串**(BE 2026-08-17)—— 整串塞進 `background`
  // 是無效宣告(色點會變透明,而且不會有任何錯誤)。取第一個合法 HEX,查無走中性色。
  const brandChipColor = brandPrimaryHex(brandKit?.colors) ?? 'var(--ink-600)';

  // R51 D3 v2 — 卡片底部 meta line:「品牌規範 · N 件素材 · 更新時間」
  // T12(docs/50):素材數讀 `project.assetCount`(BE list 回真值),恆顯(與
  // brandKit 是否綁定無關;之前因 BE 未提供而 gate 在 brandKit 後的 hack 已移除)。
  const assetsCountLabel = t('list.card.assetsCount', { count: project.assetCount });
  return (
    // 整卡可點對齊 D3 (3) M-12 mockup;改 div + role="button" 滿足 jsx-a11y
    // (article 帶 role="button" 違規;div 是 generic 沒 constraint)。
    <div
      data-testid="project-card"
      data-project-id={project.projectId}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        background: 'var(--ink-900)',
        border: `1px solid ${hover ? 'var(--border-hover)' : 'var(--hairline-strong)'}`,
        borderRadius: 'var(--r-lg)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: hover ? 'var(--shadow-md)' : 'var(--shadow-sm)',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'transform 0.12s, box-shadow 0.12s, border-color 0.12s',
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      {/* R245 — 封面三態走 CoverSurface;`coverBg` 降級成 kind==='none' 的 fallback */}
      <CoverSurface
        data-testid="project-card-cover"
        cover={project.cover}
        fallback={coverBg}
        style={{ width: '100%', aspectRatio: '5 / 3' }}
      >
        {/* Kebab menu(右上角);cover wrapper 不可 aria-hidden(否則 testing-library 視 kebab button 為不可見)*/}
        <div style={{ position: 'absolute', top: 8, right: 8 }}>
          <KebabMenu
            open={menuOpen}
            setOpen={setMenuOpen}
            menuRef={menuRef}
            onOpen={onOpen}
            onEdit={onEdit}
            onDelete={onDelete}
            deleting={deleting}
          />
        </div>
      </CoverSurface>
      <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h3
          style={{
            margin: 0,
            color: 'var(--ink-100)',
            fontSize: 'var(--text-md)',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {project.name}
        </h3>

        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-xs)',
            // CIS 文字只有兩層:「（沒有簡介）」也是要讀的字 → 次文字;斜體已足以標示是佔位
            color: 'var(--ink-500)',
            fontStyle: project.description ? 'normal' : 'italic',
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {project.description ?? t('list.card.noDescription')}
        </p>

        {/* meta row:「品牌規範 · N 件素材 · 更新時間」(D3 `.pj-meta`)。
            CIS:可讀內文 ≥ 11px;數字 / 日期走 sans + tabular-nums —— mono 只給 ID / 代碼,
            這一列有中文(規範名、「件素材」、月份)。 */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            marginTop: 4,
            fontSize: 11,
            color: 'var(--ink-500)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {/* R397 — 品牌規範 UI 隱藏(brandKitsGate):chip(含「未引用」)整顆不畫。 */}
          {BRAND_KITS_UI_VISIBLE && (brandKit ? (
            <span data-testid="project-brand-kit-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 2,
                  background: brandChipColor,
                }}
              />
              {brandKit.name}
            </span>
          ) : (
            <span data-testid="project-no-brand-kit" style={{ color: 'var(--ink-500)' }}>
              {t('list.card.noBrandKit')}
            </span>
          ))}
          {BRAND_KITS_UI_VISIBLE && <span aria-hidden style={{ color: 'var(--ink-700)' }}>·</span>}
          <span data-testid="project-asset-count">{assetsCountLabel}</span>
          <span aria-hidden style={{ color: 'var(--ink-700)' }}>·</span>
          <span data-testid="project-updated-at">{dstamp(project.updatedDatetime)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * M-12 列表檢視單列 — view='list' 時渲染。
 * 左側小封面縮圖(沿用 cover gradient)+ 名稱 / 簡介 + 右側 meta(品牌規範 chip /
 * 素材數 / 更新時間)+ kebab。整列可點進畫布,行為對齊 ProjectCard。
 * mockup 只給網格稿,列表呈現沿用同一設計系統 token,保持視覺一致。
 */
export function ProjectRow({ project, brandKit, onOpen, onEdit, onDelete, deleting }: ProjectItemProps) {
  const { t } = useTranslation('projects');
  const { dstamp } = useLibraryDateFormat();
  const [hover, setHover] = useState(false);
  const { menuOpen, setMenuOpen, menuRef } = useKebabMenu();
  const coverBg = pickCoverGradient(brandKit?.colors.primary);
  // ⚠️ `colors.primary` 是 **CSV 字串**(BE 2026-08-17)—— 整串塞進 `background`
  // 是無效宣告(色點會變透明,而且不會有任何錯誤)。取第一個合法 HEX,查無走中性色。
  const brandChipColor = brandPrimaryHex(brandKit?.colors) ?? 'var(--ink-600)';

  return (
    <div
      data-testid="project-card"
      data-project-id={project.projectId}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 16px',
        background: 'var(--ink-900)',
        border: `1px solid ${hover ? 'var(--border-hover)' : 'var(--hairline-strong)'}`,
        borderRadius: 'var(--r-sm)',
        boxShadow: hover ? 'var(--shadow-sm)' : 'none',
        cursor: 'pointer',
        transition: 'border-color 0.12s, box-shadow 0.12s',
      }}
    >
      {/* 小封面縮圖 — 名稱就在右邊,這顆純裝飾 → `alt=""`(原本掛在 wrapper 上的
          aria-hidden 拿掉:img 已是 presentational,容器本身沒有文字也沒有可聚焦
          內容,對 a11y tree 的貢獻本來就是零)*/}
      <CoverSurface
        data-testid="project-card-cover"
        cover={project.cover}
        fallback={coverBg}
        style={{ width: 56, height: 40, flexShrink: 0, borderRadius: 6 }}
      />

      {/* 名稱 + 簡介 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            color: 'var(--ink-100)',
            fontSize: 'var(--text-md)',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {project.name}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 'var(--text-xs)',
            // CIS 文字只有兩層:「（沒有簡介）」也是要讀的字 → 次文字;斜體已足以標示是佔位
            color: 'var(--ink-500)',
            fontStyle: project.description ? 'normal' : 'italic',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {project.description ?? t('list.card.noDescription')}
        </div>
      </div>

      {/* meta:品牌規範 chip / 素材數 / 更新時間 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
          fontSize: 11,
          color: 'var(--ink-500)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {/* R397 — 品牌規範 UI 隱藏:chip 與分隔符一起收。 */}
        {BRAND_KITS_UI_VISIBLE && (brandKit ? (
          <span data-testid="project-brand-kit-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span
              aria-hidden
              style={{ width: 6, height: 6, borderRadius: 2, background: brandChipColor }}
            />
            {brandKit.name}
          </span>
        ) : (
          <span data-testid="project-no-brand-kit" style={{ color: 'var(--ink-500)' }}>
            {t('list.card.noBrandKit')}
          </span>
        ))}
        {BRAND_KITS_UI_VISIBLE && <span aria-hidden style={{ color: 'var(--ink-700)' }}>·</span>}
        <span data-testid="project-asset-count">{t('list.card.assetsCount', { count: project.assetCount })}</span>
        <span aria-hidden style={{ color: 'var(--ink-700)' }}>·</span>
        <span data-testid="project-updated-at">{dstamp(project.updatedDatetime)}</span>
      </div>

      <KebabMenu
        open={menuOpen}
        setOpen={setMenuOpen}
        menuRef={menuRef}
        onOpen={onOpen}
        onEdit={onEdit}
        onDelete={onDelete}
        deleting={deleting}
      />
    </div>
  );
}
